import type { DateTime } from "luxon";
import type { AiPrintoutDesignContext } from "../../../downloads/aiPrintout/designContext";

/**
 * The `/printout/ai-templates` contract (`backend/models/printout_templates.py`). Request bodies are strict on
 * the server: every key is sent, optional ones as `null`, and nothing else.
 */

/** Mirrors `CUSTOM_PRINTOUT_NAME_MAX_LENGTH`. */
export const CUSTOM_PRINTOUT_NAME_MAX_LENGTH = 40;
/** Mirrors `MAX_LIVE_PRINTOUTS_PER_DEPARTMENT`. */
export const MAX_CUSTOM_PRINTOUTS = 10;
/** Mirrors `REPAIR_MESSAGE_MAX_LENGTH`. */
export const REPAIR_MESSAGE_MAX_LENGTH = 500;

/** One request and Annie's one-line summary of what she changed. */
export type CustomPrintoutTurn = {
	request: string;
	summary: string;
	isRepair: boolean;
	hasAttachment: boolean;
};

/** Who asked for a saved turn. Formatted with the department's name order; `lastName` is null for an admin with no staff record. */
export type CustomPrintoutTurnAuthor = { userId: string; firstName: string; lastName: string | null };

/** A turn of a saved printout's conversation. `author` is null once their account is gone (or the turn predates authors). */
export type CustomPrintoutSavedTurn = CustomPrintoutTurn & { author: CustomPrintoutTurnAuthor | null };

/**
 * What a design shows. Mirrors `CustomPrintoutCoverage`. `every_shift`: everyone working the day, which the preview
 * checks before it prints. `summary`: totals and counts instead of a row per person, which the user confirmed; the
 * preview checks only that it draws.
 */
export type CustomPrintoutCoverage = "every_shift" | "summary";

/** A row of the printout list. `updatedAt` arrives as a DateTime (`convertIsoDates`). */
export type CustomPrintoutSummary = {
	id: string;
	name: string;
	kind: "html";
	versionNumber: number;
	summary: string;
	/** The current version's. */
	coverage: CustomPrintoutCoverage;
	updatedAt: DateTime;
};

/**
 * A printout's version with its template source: the current one, or an earlier one asked for by id. Name, kind and
 * `updatedAt` are the printout's.
 */
export type CustomPrintoutDetail = CustomPrintoutSummary & {
	versionId: string;
	contractVersion: number;
	source: string;
	conversation: CustomPrintoutSavedTurn[];
};

/** A row of a printout's version history, newest first. `createdAt` arrives as a DateTime (`convertIsoDates`). */
export type CustomPrintoutVersionSummary = {
	id: string;
	versionNumber: number;
	summary: string;
	createdAt: DateTime;
	/** Who saved or restored it; null once their account is gone. */
	author: CustomPrintoutTurnAuthor | null;
	/** Set on a version that restored an earlier one: that version's number. */
	restoredFromVersionNumber: number | null;
	isCurrent: boolean;
};

/** Why the browser couldn't use a template: the harness's failures plus a failed completeness check. */
export type CustomPrintoutFailureKind = "syntax" | "runtime" | "timeout" | "bad-output" | "too-large" | "incomplete" | "print-media";

/** A shift the template left off the page, by category and time — never by person. */
export type CustomPrintoutMissingShift = {
	role: string | null;
	job: string | null;
	location: string | null;
	time: string | null;
};

/**
 * How much Annie thinks before answering a design request, lowest first. Mirrors `CustomPrintoutEffort`; the server
 * refuses anything else.
 */
export const CUSTOM_PRINTOUT_EFFORTS = ["low", "medium", "high"] as const;
export type CustomPrintoutEffort = (typeof CUSTOM_PRINTOUT_EFFORTS)[number];

export type CustomPrintoutRepair = {
	kind: CustomPrintoutFailureKind;
	/** A fixed description per kind; never the template's own error text, which could quote a person. */
	message: string;
	missing: CustomPrintoutMissingShift[];
};

export type StartCustomPrintoutGenerationRequest = {
	departmentId: string;
	printoutId: string | null;
	prompt: string | null;
	fileKey: string | null;
	designContext: AiPrintoutDesignContext;
	baseVersionId: string | null;
	baseGenerationId: string | null;
	repair: CustomPrintoutRepair | null;
	effort: CustomPrintoutEffort;
	/** The user confirmed this printout may be a summary: they answered Annie's question, or the base is one. */
	allowsSummary: boolean;
};

/** `awaiting_confirmation`: the request reads as a summary; Annie asks before designing anything. */
export type CustomPrintoutGenerationStatus = "running" | "succeeded" | "failed" | "awaiting_confirmation";

export type CustomPrintoutGenerationResponse = {
	generationId: string;
	status: CustomPrintoutGenerationStatus;
	contractVersion: number;
	source: string | null;
	/** Set when succeeded. */
	coverage: CustomPrintoutCoverage | null;
	/** Set when succeeded (what changed), and when awaiting confirmation (the summary sheet Annie would design). */
	summary: string | null;
	/** Set when failed: the i18n key to show. */
	errorKey: string | null;
};
